import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Spin } from "antd";

// Stores
import authStore from "./stores/authStore";
import cartStore from "./stores/cartStore";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Pages (lazy loaded - كل صفحة بتتحمل بشكل منفصل)
const HomePage = React.lazy(() => import("./pages/Home/HomePage"));
const ProductListPage = React.lazy(() => import("./pages/Product/ProductListPage"));
const ProductDetailPage = React.lazy(() => import("./pages/Product/ProductDetailPage"));
const CartPage = React.lazy(() => import("./pages/Cart/CartPage"));
const CheckoutPage = React.lazy(() => import("./pages/Checkout/CheckoutPage"));
const OrderSuccessPage = React.lazy(() => import("./pages/Orders/OrderSuccessPage"));
const MyOrdersPage = React.lazy(() => import("./pages/Orders/MyOrdersPage"));
const OrderDetailPage = React.lazy(() => import("./pages/Orders/OrderDetailPage"));
const LoginPage = React.lazy(() => import("./pages/Auth/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/Auth/RegisterPage"));
const AccountPage = React.lazy(() => import("./pages/Account/AccountPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const PortfolioLayout = React.lazy(() => import("./pages/Portfolio/PortfolioLayout"));
const PortfolioHome = React.lazy(() => import("./pages/Portfolio/PortfolioHome"));
const ProjectsPage = React.lazy(() => import("./pages/Portfolio/ProjectsPage"));
const ProjectDetailPage = React.lazy(() => import("./pages/Portfolio/ProjectDetailPage"));

import { initPixel, pageView } from "./utils/pixel";
import {
  initTikTokPixel,
  pageView as tikTokPageView,
} from "./utils/tiktokPixel";

// شاشة تحميل بسيطة تظهر أثناء تحميل أي صفحة lazy
const PageLoader = () => (
  <div
    className="flex justify-center items-center"
    style={{ minHeight: "60vh" }}
  >
    <Spin size="large" />
  </div>
);

// ← تتبع تغيير الصفحات + ScrollToTop
const PixelPageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    // Facebook PageView
    pageView();

    // TikTok PageView
    tikTokPageView();
  }, [location.pathname]);

  return null;
};

// Protected Route للصفحات اللي تحتاج login
const ProtectedRoute = observer(({ children }) => {
  if (!authStore.isHydrated) return null;
  if (!authStore.isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
});

// Guest Route (login/register) → لو مسجل روّح للـ home
const GuestRoute = observer(({ children }) => {
  if (!authStore.isHydrated) return null;
  if (authStore.isLoggedIn) {
    return <Navigate to="/" replace />;
  }
  return children;
});

const App = observer(() => {
  useEffect(() => {
    cartStore.fetchCart();

    // Facebook Pixel
    initPixel();

    // TikTok Pixel
    initTikTokPixel();
  }, []);

  return (
    <BrowserRouter>
      <PixelPageTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/portfolio" element={<PortfolioLayout />}>
              <Route index element={<PortfolioHome />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailPage />} />
            </Route>
          <Route path="/" element={<MainLayout />}>
            {/* Public */}
            <Route index element={<HomePage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="products" element={<ProductListPage />} />
            <Route path="products/:slug" element={<ProductDetailPage />} />
            <Route path="cart" element={<CartPage />} />

            

            {/* Checkout (Guest + User) */}
            <Route path="checkout" element={<CheckoutPage />} />
            <Route
              path="order-success/:orderNumber"
              element={<OrderSuccessPage />}
            />

            {/* Order tracking (Guest بـ order number) */}
            <Route path="orders/:orderNumber" element={<OrderDetailPage />} />

            {/* Protected */}
            <Route
              path="my-orders"
              element={
                <ProtectedRoute>
                  <MyOrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />

            {/* Auth */}
            <Route
              path="login"
              element={
                <GuestRoute>
                  <LoginPage />
                </GuestRoute>
              }
            />
            <Route
              path="register"
              element={
                <GuestRoute>
                  <RegisterPage />
                </GuestRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
});

export default App;